import React, { useState, useEffect } from 'react';
import { ClipboardCheck, Plus, X, ChevronDown, ChevronUp, KeyRound, Copy, FileText } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const FLAG_LABELS = { puntos: 'Recuperación de puntos', empresa: 'Empresa', familiar: 'Familiar', recomendado: 'Recomendado' };
const STATUS_STYLES = {
  pendiente: 'bg-amber-100 text-amber-700',
  espera: 'bg-sky-100 text-sky-700',
  aceptado: 'bg-teal-100 text-teal-700',
  'no-disponible': 'bg-stone-100 text-stone-500',
};
const PRIORITY_STYLES = { alta: 'text-red-600', media: 'text-amber-600', baja: 'text-stone-400' };
const EMPTY = { name: '', phone: '', email: '', residence: '', course: '', theoryPassed: false, fromOtherSchool: false, transfer: false, startWhen: 'Lo antes posible', source: 'telefono', notes: '' };
const ADM_STATES = [
  { id: 'abiertas', label: 'Admisiones abiertas' },
  { id: 'espera', label: 'Lista de espera activa' },
  { id: 'pausadas', label: 'Admisiones temporalmente pausadas' },
];

export default function AdminAdmisiones() {
  const [admissions, setAdmissions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sources, setSources] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [invites, setInvites] = useState({});
  const [adocs, setAdocs] = useState({});

  useEffect(() => {
    if (expanded) api.getAdmissionDocuments(expanded).then((d) => setAdocs((p) => ({ ...p, [expanded]: d }))).catch(() => {});
  }, [expanded]);

  const generateInvite = async (a) => {
    try {
      const inv = await api.createAdmissionInvite(a.id);
      setInvites((p) => ({ ...p, [a.id]: inv }));
      toast.success('Acceso generado. Copia el enlace y el PIN para el alumno.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar el acceso');
    }
  };

  const downloadDoc = async (d) => {
    try {
      const blob = await api.adminDocBlob(d.id);
      const url = URL.createObjectURL(blob);
      const el = document.createElement('a');
      el.href = url; el.download = d.fileName; el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('No se pudo descargar'); }
  };
  const [creating, setCreating] = useState(null);
  const [admState, setAdmState] = useState('abiertas');

  useEffect(() => {
    api.getAdmissions().then(setAdmissions).catch(() => {});
    api.getScmeSettings().then((s) => setAdmState(s.admissionsState || 'abiertas')).catch(() => {});
    api.getValueLists().then((lists) => {
      setStatuses(lists.find((l) => l.id === 'estados-admision')?.values || []);
      setSources(lists.find((l) => l.id === 'origenes-admision')?.values || []);
      setPriorities(lists.find((l) => l.id === 'prioridades-admision')?.values || []);
    }).catch(() => {});
  }, []);

  const update = async (a, data) => {
    try {
      const updated = await api.updateAdmission(a.id, data);
      setAdmissions((prev) => prev.map((x) => x.id === a.id ? updated : x));
    } catch { toast.error('Error al actualizar'); }
  };

  const create = async () => {
    if (!creating.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (!creating.phone.trim() && !creating.email.trim()) { toast.error('Indica teléfono o email'); return; }
    try {
      const doc = await api.createAdmission(creating);
      setAdmissions((prev) => [doc, ...prev]);
      setCreating(null);
      toast.success('Solicitud registrada');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear'); }
  };

  const changeAdmState = async (value) => {
    try {
      const updated = await api.updateScmeSettings({ admissionsState: value });
      setAdmState(updated.admissionsState);
      toast.success(`Estado global: ${ADM_STATES.find((s) => s.id === value)?.label}`);
    } catch { toast.error('Error al cambiar el estado global'); }
  };

  const label = (list, id) => list.find((v) => v.id === id)?.label || id;
  const shown = filter ? admissions.filter((a) => a.status === filter) : admissions;

  return (
    <div data-testid="admin-admisiones">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-amber-500" />
            <h1 className="text-2xl font-black text-stone-800">Admisiones</h1>
          </div>
          <p className="text-sm text-stone-500">Natur Autoescuela no vende plazas automáticamente: cada solicitud se revisa según capacidad real. Preparado para clasificación automática por ANA.</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={admState} onChange={(e) => changeAdmState(e.target.value)} className={`border rounded-full px-4 py-2 text-xs font-bold bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 ${admState === 'abiertas' ? 'border-teal-300 text-teal-700' : admState === 'espera' ? 'border-sky-300 text-sky-700' : 'border-amber-300 text-amber-700'}`} data-testid="admissions-global-state">
            {ADM_STATES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <button onClick={() => setCreating({ ...EMPTY })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-admission-button">
            <Plus className="w-4 h-4" /> Registrar solicitud
          </button>
        </div>
      </div>

      {/* Filtros por estado */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setFilter('')} className={`px-4 py-1.5 text-xs font-bold rounded-full border transition-colors ${!filter ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300'}`} data-testid="filter-all">
          Todas ({admissions.length})
        </button>
        {statuses.map((s) => (
          <button key={s.id} onClick={() => setFilter(s.id)} className={`px-4 py-1.5 text-xs font-bold rounded-full border transition-colors ${filter === s.id ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300'}`} data-testid={`filter-${s.id}`}>
            {s.label} ({admissions.filter((a) => a.status === s.id).length})
          </button>
        ))}
      </div>

      {/* Alta manual (teléfono, presencial, etc.) */}
      {creating && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-6" data-testid="admission-create-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black text-stone-800">Registrar solicitud manual</h2>
            <button onClick={() => setCreating(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} placeholder="Nombre *" className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="create-admission-name" />
            <input value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} placeholder="Teléfono" className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="create-admission-phone" />
            <input value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} placeholder="Email" className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <input value={creating.residence} onChange={(e) => setCreating({ ...creating, residence: e.target.value })} placeholder="Residencia" className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <input value={creating.course} onChange={(e) => setCreating({ ...creating, course: e.target.value })} placeholder="Permiso o curso" className="border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <select value={creating.source} onChange={(e) => setCreating({ ...creating, source: e.target.value })} className="border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="create-admission-source">
              {sources.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setCreating(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
            <button onClick={create} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-admission-button">Registrar</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {shown.map((a) => (
          <div key={a.id} className="bg-white rounded-lg border border-stone-200" data-testid={`admission-row-${a.id}`}>
            <div className="p-4 flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-48">
                <p className="font-bold text-stone-800">{a.name}</p>
                <p className="text-xs text-stone-400">{[a.phone, a.email].filter(Boolean).join(' · ')}</p>
                <p className="text-xs text-teal-600">{a.course || 'Sin curso indicado'} · {a.residence}</p>
              </div>
              <span className="text-[10px] font-bold bg-stone-100 text-stone-500 px-2 py-1 rounded uppercase">{label(sources, a.source)}</span>
              <select value={a.priority} onChange={(e) => update(a, { priority: e.target.value })} className={`border border-stone-200 rounded px-2 py-1.5 text-xs font-bold bg-white focus:outline-none ${PRIORITY_STYLES[a.priority] || ''}`} data-testid={`priority-${a.id}`}>
                {priorities.map((p) => <option key={p.id} value={p.id}>Prioridad {p.label}</option>)}
              </select>
              <select value={a.status} onChange={(e) => update(a, { status: e.target.value })} className={`rounded px-3 py-1.5 text-xs font-bold border-0 focus:outline-none ${STATUS_STYLES[a.status] || 'bg-stone-100'}`} data-testid={`status-${a.id}`}>
                {statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <span className="text-xs text-stone-400 w-20">{new Date(a.createdAt).toLocaleDateString('es-ES')}</span>
              <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} className="text-stone-400 hover:text-stone-700 transition-colors" data-testid={`expand-${a.id}`}>
                {expanded === a.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {expanded === a.id && (
              <div className="border-t border-stone-100 p-4 bg-stone-50 rounded-b-lg" data-testid={`admission-detail-${a.id}`}>
                {a.status === 'aceptado' && (
                  <div className="bg-white border border-amber-200 rounded p-3 mb-4" data-testid={`portal-invite-panel-${a.id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <KeyRound className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-bold text-stone-700 flex-1">Área de Alumno (acceso por invitación)</span>
                      <button onClick={() => generateInvite(a)} className="btn-natur px-3 py-1.5 rounded text-xs font-bold" data-testid={`generate-invite-${a.id}`}>
                        {invites[a.id] ? 'Regenerar acceso' : 'Generar acceso'}
                      </button>
                    </div>
                    {invites[a.id] && (
                      <div className="mt-3 space-y-1.5 text-xs" data-testid={`invite-result-${a.id}`}>
                        <p className="text-stone-600 break-all"><strong>Enlace mágico:</strong> {window.location.origin}{invites[a.id].link}</p>
                        <p className="text-stone-600"><strong>Código PIN (6 dígitos):</strong> <span className="font-black tracking-widest text-amber-600">{invites[a.id].pin}</span> · caduca en 7 días · un solo uso</p>
                        <button onClick={() => { navigator.clipboard.writeText(`Activa tu Área de Alumno: ${window.location.origin}${invites[a.id].link}\nCódigo PIN: ${invites[a.id].pin}`); toast.success('Enlace y PIN copiados'); }} className="flex items-center gap-1.5 text-teal-600 font-bold hover:underline" data-testid={`copy-invite-${a.id}`}>
                          <Copy className="w-3 h-3" /> Copiar enlace + PIN para enviárselo al alumno
                        </button>
                      </div>
                    )}
                    {(adocs[a.id] || []).length > 0 && (
                      <div className="mt-3 border-t border-stone-100 pt-2">
                        <p className="text-xs font-bold text-stone-700 mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Documentación subida por el alumno:</p>
                        {adocs[a.id].map((d) => (
                          <button key={d.id} onClick={() => downloadDoc(d)} className="block text-xs text-teal-600 hover:underline" data-testid={`admin-doc-${d.id}`}>{d.fileName}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-stone-600 mb-4">
                  <p><span className="font-bold">Teórico aprobado:</span> {a.theoryPassed ? 'Sí' : 'No'}</p>
                  <p><span className="font-bold">Otra autoescuela:</span> {a.fromOtherSchool ? 'Sí' : 'No'}</p>
                  <p><span className="font-bold">Traslado expediente:</span> {a.transfer ? 'Sí' : 'No'}</p>
                  <p><span className="font-bold">Comenzar:</span> {a.startWhen || '—'}</p>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(FLAG_LABELS).map(([k, l]) => (
                    <button
                      key={k}
                      onClick={() => update(a, { flags: { ...a.flags, [k]: !a.flags?.[k] } })}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${a.flags?.[k] ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-500 border-stone-300'}`}
                      data-testid={`flag-${k}-${a.id}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
                <textarea
                  defaultValue={a.notes}
                  onBlur={(e) => e.target.value !== a.notes && update(a, { notes: e.target.value })}
                  placeholder="Observaciones especiales…"
                  rows={2}
                  className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid={`notes-${a.id}`}
                />
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && <p className="text-center text-stone-400 py-12 bg-white rounded-lg border border-stone-200">No hay solicitudes{filter ? ' en este estado' : ' todavía'}.</p>}
      </div>
    </div>
  );
}
